import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import type { OptimisticLocalStore } from "convex/browser";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import {
  Brain,
  Clock,
  Copy,
  Heart,
  Star,
  Sunrise,
  UtensilsCrossed,
  Zap,
} from "lucide-react";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { FoodMemoryCard } from "../components/FoodMemoryCard";
import {
  CustomFoodRow,
  EntryRow,
  FavoriteRow,
  LibrarySection,
  RecipeRow,
  SavedMealRow,
  SectionBody,
} from "../components/library/LibraryRows";
import {
  CopyMealSheet,
  EntityEditSheet,
  DeleteConfirmSheet,
  MemoryFormSheet,
  type EntityEditPayload,
  type EntityEditTarget,
  type MemorySavePayload,
} from "../components/library/LibrarySheets";
import { useLoggingFlow } from "../lib/logging-flow";
import type { LoggingFlowReviewItem } from "../lib/logging-flow";
import { usePlate } from "../lib/plate";
import type { NewPlateItem } from "../lib/plate";
import { addDays, localDayStartMs } from "../lib/dayHelpers";
import type {
  CustomFoodView,
  DeleteTarget,
  FavoriteToggleTarget,
  FavoriteView,
  FrequentFoodView,
  LibraryEntry,
  MemoryView,
  RecipeView,
  SavedMealView,
} from "./library-utils";
import {
  anyQuickActionAvailable,
  buildUsualLunchItems,
  customFoodToReviewItem,
  entryToReviewItem,
  favoriteToReviewItem,
  favoriteViewFrom,
  filterRows,
  groupLoggedMeals,
  mealTypeItems,
  memoryToReviewItem,
  patchRowById,
  quickActionAvailability,
  recipePerServing,
  recipeToReviewItem,
  removeRowById,
  reviewItemToPlateItem,
  savedMealToReviewItems,
  startReviewWithItems,
  toggleFavoriteInRows,
  upsertFavoriteView,
} from "./library-utils";

// Food Library screen (plan todo 19) — absorbs the old Memory screen. Every
// "Log again" / quick action PREFILLS the Meal Review flow via
// startFlow("review", {items, source:"recent"}); nothing persists until the
// user presses Log meal in the review screen (review-before-save invariant).
// Legacy e2e testids kept on purpose: the global search input is
// "memory-search-input", the Memories section wrapper is "memory-container",
// and the memory add form keeps "memory-add-*".

export interface LibraryScreenProps {
  readonly onSwitchScreen?: (screen: string) => void;
}

export type LibraryEditTarget =
  | { readonly kind: "memory-add" }
  | { readonly kind: "memory"; readonly memory: MemoryView }
  | { readonly kind: "savedMeal"; readonly meal: SavedMealView }
  | { readonly kind: "customFood"; readonly food: CustomFoodView }
  | { readonly kind: "recipe"; readonly recipe: RecipeView };

export interface LibraryViewProps {
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly recentFoods: readonly LibraryEntry[] | undefined;
  readonly frequentFoods: readonly FrequentFoodView[] | undefined;
  readonly savedMeals: readonly SavedMealView[] | undefined;
  readonly memories: readonly MemoryView[] | undefined;
  readonly favorites: readonly FavoriteView[] | undefined;
  readonly customFoods: readonly CustomFoodView[] | undefined;
  readonly recipes: readonly RecipeView[] | undefined;
  readonly yesterdayEntries: readonly LibraryEntry[] | undefined;
  readonly busy?: boolean;
  readonly onStartReview: (items: readonly LoggingFlowReviewItem[]) => void;
  readonly onAddToPlate: (item: NewPlateItem) => void;
  readonly onToggleFavorite: (target: FavoriteToggleTarget) => void;
  readonly onSaveMemory: (payload: MemorySavePayload) => Promise<boolean> | boolean;
  readonly onDeleteMemory: (id: string) => Promise<boolean> | boolean;
  readonly onEditEntity: (target: EntityEditTarget, payload: EntityEditPayload) => Promise<boolean> | boolean;
  readonly onDuplicate: (kind: "customFood" | "recipe", id: string) => void;
  readonly onDelete: (target: DeleteTarget) => Promise<boolean> | boolean;
  // Test hooks (Plan initialRangeId precedent): render sheets without clicks.
  readonly initialEditTarget?: LibraryEditTarget;
  readonly initialDeleteTarget?: DeleteTarget;
  readonly initialCopySheetOpen?: boolean;
}

function entityEditTarget(target: LibraryEditTarget): EntityEditTarget | null {
  switch (target.kind) {
    case "savedMeal":
      return { kind: "savedMeal", id: target.meal._id, name: target.meal.name };
    case "customFood":
      return {
        kind: "customFood",
        id: target.food._id,
        name: target.food.name,
        servingUnit: target.food.servingUnit,
        ...(target.food.calories === undefined ? {} : { calories: target.food.calories }),
      };
    case "recipe":
      return {
        kind: "recipe",
        id: target.recipe._id,
        name: target.recipe.name,
        ...(target.recipe.servings === undefined ? {} : { servings: target.recipe.servings }),
      };
    default:
      return null;
  }
}

const quickActionButtonClass =
  "flex min-h-[44px] items-center gap-3 rounded-xl border border-border bg-surface px-4 py-3 text-left text-sm font-medium text-text active:scale-[0.98] select-none";

export function LibraryView(props: LibraryViewProps) {
  const {
    search,
    onSearchChange,
    recentFoods,
    frequentFoods,
    savedMeals,
    memories,
    favorites,
    customFoods,
    recipes,
    yesterdayEntries,
    busy = false,
    onStartReview,
    onAddToPlate,
    onToggleFavorite,
    onSaveMemory,
    onDeleteMemory,
    onEditEntity,
    onDuplicate,
    onDelete,
  } = props;

  const [editTarget, setEditTarget] = useState<LibraryEditTarget | null>(
    props.initialEditTarget ?? null,
  );
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(
    props.initialDeleteTarget ?? null,
  );
  const [copySheetOpen, setCopySheetOpen] = useState(props.initialCopySheetOpen ?? false);
  const activeEntityEdit = editTarget === null ? null : entityEditTarget(editTarget);

  const searching = search.trim().length > 0;
  const filteredRecent = recentFoods === undefined ? undefined : filterRows(recentFoods, search, (r) => r.name);
  const filteredFrequent = frequentFoods === undefined ? undefined : filterRows(frequentFoods, search, (r) => r.name);
  const filteredSavedMeals = savedMeals === undefined ? undefined : filterRows(savedMeals, search, (r) => r.name);
  const filteredMemories = memories === undefined ? undefined : filterRows(memories, search, (r) => r.foodName);
  const filteredFavorites = favorites === undefined ? undefined : filterRows(favorites, search, (r) => r.name);
  const filteredCustomFoods = customFoods === undefined ? undefined : filterRows(customFoods, search, (r) => r.name);
  const filteredRecipes = recipes === undefined ? undefined : filterRows(recipes, search, (r) => r.name);

  const availability = quickActionAvailability({ yesterdayEntries, frequentFoods });
  const copyOptions = groupLoggedMeals(yesterdayEntries ?? []);

  const closeSheets = () => {
    setEditTarget(null);
    setDeleteTarget(null);
  };

  const reviewEntry = (entry: LibraryEntry) => onStartReview([entryToReviewItem(entry)]);
  const plateEntry = (entry: LibraryEntry) =>
    onAddToPlate(reviewItemToPlateItem(entryToReviewItem(entry)));

  const handleRepeatBreakfast = () => {
    console.log("[Frontend][Library] Quick action: repeat yesterday's breakfast");
    onStartReview(mealTypeItems(yesterdayEntries ?? [], "breakfast"));
  };
  const handleUsualLunch = () => {
    console.log("[Frontend][Library] Quick action: log usual lunch");
    onStartReview(buildUsualLunchItems(frequentFoods ?? []));
  };
  const handleCopyPick = (mealType: string) => {
    console.log("[Frontend][Library] Quick action: copy meal", { mealType });
    const option = copyOptions.find((o) => o.mealType === mealType);
    setCopySheetOpen(false);
    if (option !== undefined) onStartReview(option.items);
  };

  return (
    <div data-testid="library-container" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-text">Food Library</h2>
        <p className="text-sm text-text-muted">Everything you eat, ready to log again.</p>
      </div>

      {/* Global search — filters every local section (300ms debounce lives in
          the wired screen). Legacy testid memory-search-input kept for e2e. */}
      <Input
        data-testid="memory-search-input"
        placeholder="Search your library..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />

      {anyQuickActionAvailable(availability) && (
        <details open data-testid="library-quick-actions" className="rounded-2xl border border-border bg-surface">
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center gap-2 px-4 py-3 select-none">
            <Zap size={16} className="text-accent" />
            <span className="text-sm font-semibold text-text">Quick actions</span>
          </summary>
          <div className="flex flex-col gap-2 border-t border-border px-3 py-3">
            {availability.repeatBreakfast && (
              <button
                type="button"
                data-testid="qa-repeat-breakfast"
                className={quickActionButtonClass}
                onClick={handleRepeatBreakfast}
              >
                <Sunrise size={18} className="text-accent" />
                Repeat yesterday&apos;s breakfast
              </button>
            )}
            {availability.copyMeal && (
              <button
                type="button"
                data-testid="qa-copy-meal"
                className={quickActionButtonClass}
                onClick={() => setCopySheetOpen(true)}
              >
                <Copy size={18} className="text-accent" />
                Copy a meal
              </button>
            )}
            {availability.usualLunch && (
              <button
                type="button"
                data-testid="qa-usual-lunch"
                className={quickActionButtonClass}
                onClick={handleUsualLunch}
              >
                <UtensilsCrossed size={18} className="text-accent" />
                Log usual lunch
              </button>
            )}
          </div>
        </details>
      )}

      <LibrarySection testId="library-recent-foods" title="Recent foods" count={filteredRecent?.length}>
        <SectionBody
          sectionKey="recent-foods"
          data={filteredRecent}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={Clock}
              title="No recent foods"
              message="Foods you log will show up here for fast re-logging."
            />
          }
          renderRows={() =>
            (filteredRecent ?? []).map((entry) => (
              <EntryRow
                key={entry._id}
                entry={entry}
                onLogAgain={reviewEntry}
                onAddToPlate={plateEntry}
              />
            ))
          }
        />
      </LibrarySection>

      <LibrarySection testId="library-frequent-foods" title="Frequent foods" count={filteredFrequent?.length}>
        <SectionBody
          sectionKey="frequent-foods"
          data={filteredFrequent}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={UtensilsCrossed}
              title="No frequent foods yet"
              message="Log the same food a couple of times and it becomes a shortcut here."
            />
          }
          renderRows={() =>
            (filteredFrequent ?? []).map((entry) => (
              <EntryRow
                key={entry._id}
                entry={entry}
                count={entry.count}
                onLogAgain={reviewEntry}
                onAddToPlate={plateEntry}
              />
            ))
          }
        />
      </LibrarySection>

      <LibrarySection testId="library-saved-meals" title="Saved meals" count={filteredSavedMeals?.length}>
        <SectionBody
          sectionKey="saved-meals"
          data={filteredSavedMeals}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={Heart}
              title="No saved meals"
              message="Save a meal from Today and it appears here for one-tap logging."
            />
          }
          renderRows={() =>
            (filteredSavedMeals ?? []).map((meal) => (
              <SavedMealRow
                key={meal._id}
                meal={meal}
                onLogAgain={(m) => onStartReview(savedMealToReviewItems(m))}
                onAddToPlate={(m) =>
                  savedMealToReviewItems(m).forEach((item) => onAddToPlate(reviewItemToPlateItem(item)))
                }
                onToggleFavorite={(m) =>
                  onToggleFavorite({
                    kind: "savedMeal",
                    id: m._id,
                    name: m.name,
                    favorite: m.favorite,
                    ...(m.totalCalories === undefined ? {} : { calories: m.totalCalories }),
                    ...(m.mealType === undefined ? {} : { servingLabel: m.mealType }),
                  })
                }
                onEdit={(m) => setEditTarget({ kind: "savedMeal", meal: m })}
                onDelete={(m) => setDeleteTarget({ kind: "savedMeal", id: m._id, name: m.name })}
              />
            ))
          }
        />
      </LibrarySection>

      {/* Memories section — legacy wrapper testid "memory-container". */}
      <LibrarySection testId="memory-container" title="Food memories" count={filteredMemories?.length}>
        <div className="flex justify-end">
          <Button
            data-testid="memory-add-btn"
            variant="secondary"
            size="sm"
            onClick={() => setEditTarget({ kind: "memory-add" })}
          >
            Add memory
          </Button>
        </div>
        <SectionBody
          sectionKey="memories"
          data={filteredMemories}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={Brain}
              title="No food memories yet"
              message="Add foods you eat often for faster logging."
              action={
                <Button variant="primary" size="md" onClick={() => setEditTarget({ kind: "memory-add" })}>
                  Add memory
                </Button>
              }
            />
          }
          renderRows={() =>
            (filteredMemories ?? []).map((memory) => (
              <FoodMemoryCard
                key={memory._id}
                memory={memory}
                onEdit={(m) =>
                  setEditTarget({ kind: "memory", memory: memories?.find((row) => row._id === m._id) ?? memory })
                }
                onLogAgain={(m) => onStartReview([memoryToReviewItem(m)])}
                onAddToPlate={(m) =>
                  onAddToPlate(reviewItemToPlateItem(memoryToReviewItem(m), { kind: "memory", id: m._id }))
                }
                onToggleFavorite={(m) =>
                  onToggleFavorite({
                    kind: "foodMemory",
                    id: m._id,
                    name: m.foodName,
                    favorite: m.favorite ?? false,
                    calories: m.calories,
                    ...(m.servingSize === undefined ? {} : { servingLabel: m.servingSize }),
                  })
                }
              />
            ))
          }
        />
      </LibrarySection>

      <LibrarySection testId="library-favourites" title="Favourites" count={filteredFavorites?.length}>
        <SectionBody
          sectionKey="favourites"
          data={filteredFavorites}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={Star}
              title="No favourites yet"
              message="Tap the star on any saved meal, food, recipe, or memory to pin it here."
            />
          }
          renderRows={() =>
            (filteredFavorites ?? []).map((favorite) => (
              <FavoriteRow
                key={`${favorite.kind}-${favorite.refId}`}
                favorite={favorite}
                onLogAgain={(f) => onStartReview([favoriteToReviewItem(f)])}
                onAddToPlate={(f) => onAddToPlate(reviewItemToPlateItem(favoriteToReviewItem(f)))}
                onToggleFavorite={(f) =>
                  onToggleFavorite({
                    kind: f.kind,
                    id: f.refId,
                    name: f.name,
                    favorite: true,
                    ...(f.calories === undefined ? {} : { calories: f.calories }),
                    ...(f.servingLabel === undefined ? {} : { servingLabel: f.servingLabel }),
                  })
                }
              />
            ))
          }
        />
      </LibrarySection>

      <LibrarySection testId="library-custom-foods" title="Custom foods" count={filteredCustomFoods?.length}>
        <SectionBody
          sectionKey="custom-foods"
          data={filteredCustomFoods}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={UtensilsCrossed}
              title="No custom foods"
              message="Scan a barcode or nutrition label to build your own food database."
            />
          }
          renderRows={() =>
            (filteredCustomFoods ?? []).map((food) => (
              <CustomFoodRow
                key={food._id}
                food={food}
                onLogAgain={(f) => onStartReview([customFoodToReviewItem(f)])}
                onAddToPlate={(f) =>
                  onAddToPlate(reviewItemToPlateItem(customFoodToReviewItem(f), { kind: "customFood", id: f._id }))
                }
                onToggleFavorite={(f) =>
                  onToggleFavorite({
                    kind: "customFood",
                    id: f._id,
                    name: f.name,
                    favorite: f.favorite,
                    ...(f.calories === undefined ? {} : { calories: f.calories }),
                    servingLabel: f.servingUnit,
                  })
                }
                onEdit={(f) => setEditTarget({ kind: "customFood", food: f })}
                onDuplicate={(f) => onDuplicate("customFood", f._id)}
                onDelete={(f) => setDeleteTarget({ kind: "customFood", id: f._id, name: f.name })}
              />
            ))
          }
        />
      </LibrarySection>

      <LibrarySection testId="library-recipes" title="Recipes" count={filteredRecipes?.length}>
        <SectionBody
          sectionKey="recipes"
          data={filteredRecipes}
          searching={searching}
          search={search}
          emptyState={
            <EmptyState
              icon={UtensilsCrossed}
              title="No recipes yet"
              message="Build a recipe from ingredients and log servings in one tap."
            />
          }
          renderRows={() =>
            (filteredRecipes ?? []).map((recipe) => (
              <RecipeRow
                key={recipe._id}
                recipe={recipe}
                onLogAgain={(r) => onStartReview([recipeToReviewItem(r)])}
                onAddToPlate={(r) =>
                  onAddToPlate(reviewItemToPlateItem(recipeToReviewItem(r), { kind: "recipe", id: r._id }))
                }
                onToggleFavorite={(r) =>
                  onToggleFavorite({
                    kind: "recipe",
                    id: r._id,
                    name: r.name,
                    favorite: r.favorite,
                    ...(recipePerServing(r)?.calories === undefined
                      ? {}
                      : { calories: recipePerServing(r)?.calories }),
                    ...(r.servings === undefined ? {} : { servingLabel: `${r.servings} servings` }),
                  })
                }
                onEdit={(r) => setEditTarget({ kind: "recipe", recipe: r })}
                onDuplicate={(r) => onDuplicate("recipe", r._id)}
                onDelete={(r) => setDeleteTarget({ kind: "recipe", id: r._id, name: r.name })}
              />
            ))
          }
        />
      </LibrarySection>

      {editTarget?.kind === "memory-add" && (
        <MemoryFormSheet mode="add" busy={busy} onSave={onSaveMemory} onClose={closeSheets} />
      )}
      {editTarget?.kind === "memory" && (
        <MemoryFormSheet
          mode="edit"
          memory={editTarget.memory}
          busy={busy}
          onSave={onSaveMemory}
          onDelete={() => onDeleteMemory(editTarget.memory._id)}
          onClose={closeSheets}
        />
      )}
      {activeEntityEdit !== null && (
        <EntityEditSheet
          target={activeEntityEdit}
          busy={busy}
          onSave={(payload) => onEditEntity(activeEntityEdit, payload)}
          onClose={closeSheets}
        />
      )}
      {deleteTarget !== null && (
        <DeleteConfirmSheet
          name={deleteTarget.name}
          busy={busy}
          onConfirm={() => onDelete(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      {copySheetOpen && (
        <CopyMealSheet meals={copyOptions} onPick={handleCopyPick} onClose={() => setCopySheetOpen(false)} />
      )}
    </div>
  );
}

// ── Query/mutation-wired screen ─────────────────────────────────────────────

export default function Library(_props: LibraryScreenProps) {
  const { startFlow } = useLoggingFlow();
  const { addItem } = usePlate();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchRef = useRef(debouncedSearch);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    searchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const [yesterdayStart] = useState(() => addDays(localDayStartMs(Date.now()), -1));

  const recentFoods = useQuery(api.meals.recentFoods, { limit: 20 }) as LibraryEntry[] | undefined;
  const frequentFoods = useQuery(api.meals.frequentFoods, { limit: 20 }) as FrequentFoodView[] | undefined;
  const savedMeals = useQuery(api.savedMeals.list, {}) as SavedMealView[] | undefined;
  const allMemories = useQuery(api.foodMemory.listAll, {}) as MemoryView[] | undefined;
  const searchResults = useQuery(
    api.foodMemory.search,
    debouncedSearch ? { query: debouncedSearch } : "skip",
  ) as MemoryView[] | undefined;
  const favorites = useQuery(api.savedMeals.listFavorites, {}) as FavoriteView[] | undefined;
  const customFoods = useQuery(api.customFoods.list, {}) as CustomFoodView[] | undefined;
  const recipes = useQuery(api.recipes.list, {}) as RecipeView[] | undefined;
  const yesterday = useQuery(api.dashboard.getByDate, {
    date: yesterdayStart,
    clientDayStart: yesterdayStart,
  });
  const yesterdayEntries = yesterday?.foodEntries as LibraryEntry[] | undefined;

  // ── Food memory mutations (search-aware optimistic updates, carried over
  //    from the deleted Memory screen) ──

  const patchMemoryLists = (
    localStore: OptimisticLocalStore,
    patch: (rows: Doc<"foodMemories">[]) => Doc<"foodMemories">[],
  ) => {
    const query = searchRef.current;
    const list = localStore.getQuery(api.foodMemory.listAll, {});
    if (list !== undefined) localStore.setQuery(api.foodMemory.listAll, {}, patch(list));
    if (query) {
      const searchArgs = { query };
      const searchList = localStore.getQuery(api.foodMemory.search, searchArgs);
      if (searchList !== undefined) {
        localStore.setQuery(api.foodMemory.search, searchArgs, patch(searchList));
      }
    }
  };

  const addManual = useMutation(api.foodMemory.addManual).withOptimisticUpdate((localStore, args) => {
    const now = Date.now();
    const tempId = `temp_${now}_${Math.random().toString(36).slice(2)}`;
    const newItem: Doc<"foodMemories"> = {
      _id: tempId as Id<"foodMemories">,
      _creationTime: now,
      userId: tempId as unknown as Id<"users">,
      foodNameLower: args.foodName.toLowerCase(),
      foodName: args.foodName,
      servingSize: args.servingSize,
      calories: args.calories,
      notes: args.notes,
      confidence: "high",
      source: "manual",
      createdAt: now,
      lastUsedAt: now,
      timesUsed: 0,
    };
    const list = localStore.getQuery(api.foodMemory.listAll, {});
    if (list !== undefined) localStore.setQuery(api.foodMemory.listAll, {}, [newItem, ...list]);
    const query = searchRef.current;
    if (query) {
      const searchArgs = { query };
      const searchList = localStore.getQuery(api.foodMemory.search, searchArgs);
      if (
        searchList !== undefined &&
        args.foodName.toLowerCase().includes(query.toLowerCase())
      ) {
        localStore.setQuery(api.foodMemory.search, searchArgs, [newItem, ...searchList]);
      }
    }
  });

  const updateMemory = useMutation(api.foodMemory.update).withOptimisticUpdate((localStore, args) => {
    const patch: Partial<Doc<"foodMemories">> = {
      ...(args.calories === undefined ? {} : { calories: args.calories }),
      ...(args.servingSize === undefined ? {} : { servingSize: args.servingSize }),
      ...(args.notes === undefined ? {} : { notes: args.notes }),
    };
    patchMemoryLists(localStore, (rows) => patchRowById(rows, args.id, patch));
  });

  const removeMemory = useMutation(api.foodMemory.remove).withOptimisticUpdate((localStore, args) => {
    patchMemoryLists(localStore, (rows) => removeRowById(rows, args.id));
  });

  const setMemoryFavorite = useMutation(api.foodMemory.setFavorite).withOptimisticUpdate(
    (localStore, args) => {
      patchMemoryLists(localStore, (rows) =>
        patchRowById(rows, args.id, { favorite: args.favorite }),
      );
      const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
      const memory = (allMemories ?? []).find((row) => row._id === args.id);
      if (favs !== undefined && memory !== undefined) {
        const rest = favs.filter((fav) => !(fav.kind === "foodMemory" && fav.refId === args.id));
        localStore.setQuery(
          api.savedMeals.listFavorites,
          {},
          args.favorite
            ? [
                {
                  kind: "foodMemory" as const,
                  refId: args.id,
                  name: memory.foodName,
                  calories: memory.calories,
                  servingLabel: memory.servingSize,
                },
                ...rest,
              ]
            : rest,
        );
      }
    },
  );

  // ── Saved meal mutations ──

  const renameSavedMeal = useMutation(api.savedMeals.rename).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.savedMeals.list, {});
    if (list !== undefined) {
      localStore.setQuery(api.savedMeals.list, {}, patchRowById(list, args.id, { name: args.name }));
    }
  });

  const removeSavedMeal = useMutation(api.savedMeals.remove).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.savedMeals.list, {});
    if (list !== undefined) localStore.setQuery(api.savedMeals.list, {}, removeRowById(list, args.id));
    const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
    if (favs !== undefined) {
      localStore.setQuery(
        api.savedMeals.listFavorites,
        {},
        favs.filter((fav) => !(fav.kind === "savedMeal" && fav.refId === args.id)),
      );
    }
  });

  const toggleSavedMealFavorite = useMutation(api.savedMeals.toggleFavorite).withOptimisticUpdate(
    (localStore, args) => {
      const list = localStore.getQuery(api.savedMeals.list, {});
      if (list === undefined) return;
      const row = list.find((r) => r._id === args.id);
      localStore.setQuery(api.savedMeals.list, {}, toggleFavoriteInRows(list, args.id));
      const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
      if (favs !== undefined && row !== undefined) {
        const favorited = !row.favorite;
        const rest = favs.filter((fav) => !(fav.kind === "savedMeal" && fav.refId === args.id));
        localStore.setQuery(
          api.savedMeals.listFavorites,
          {},
          favorited
            ? [
                {
                  kind: "savedMeal" as const,
                  refId: args.id,
                  name: row.name,
                  calories: row.totalCalories,
                },
                ...rest,
              ]
            : rest,
        );
      }
    },
  );

  // ── Custom food mutations ──

  const updateCustomFood = useMutation(api.customFoods.update).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.customFoods.list, {});
    if (list === undefined) return;
    localStore.setQuery(
      api.customFoods.list,
      {},
      list.map((row) =>
        row._id === args.id
          ? {
              ...row,
              ...(args.name === undefined ? {} : { name: args.name }),
              ...(args.servingUnit === undefined ? {} : { servingUnit: args.servingUnit }),
              ...(args.calories === undefined ? {} : { calories: args.calories }),
            }
          : row,
      ),
    );
  });

  const removeCustomFood = useMutation(api.customFoods.remove).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.customFoods.list, {});
    if (list !== undefined) localStore.setQuery(api.customFoods.list, {}, removeRowById(list, args.id));
    const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
    if (favs !== undefined) {
      localStore.setQuery(
        api.savedMeals.listFavorites,
        {},
        favs.filter((fav) => !(fav.kind === "customFood" && fav.refId === args.id)),
      );
    }
  });

  const toggleCustomFoodFavorite = useMutation(api.customFoods.toggleFavorite).withOptimisticUpdate(
    (localStore, args) => {
      const list = localStore.getQuery(api.customFoods.list, {});
      if (list === undefined) return;
      const row = list.find((r) => r._id === args.id);
      localStore.setQuery(api.customFoods.list, {}, toggleFavoriteInRows(list, args.id));
      const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
      if (favs !== undefined && row !== undefined) {
        const favorited = !row.favorite;
        const rest = favs.filter((fav) => !(fav.kind === "customFood" && fav.refId === args.id));
        localStore.setQuery(
          api.savedMeals.listFavorites,
          {},
          favorited
            ? [
                {
                  kind: "customFood" as const,
                  refId: args.id,
                  name: row.name,
                  calories: row.calories,
                  servingLabel: row.servingUnit,
                },
                ...rest,
              ]
            : rest,
        );
      }
    },
  );

  const createCustomFood = useMutation(api.customFoods.create).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.customFoods.list, {});
    if (list === undefined) return;
    const now = Date.now();
    const tempId = `temp_${now}_${Math.random().toString(36).slice(2)}`;
    const tempRow = {
      _id: tempId as Id<"customFoods">,
      _creationTime: now,
      userId: tempId as unknown as Id<"users">,
      name: args.name,
      nameLower: args.name.toLowerCase(),
      brand: args.brand,
      servingUnit: args.servingUnit,
      servingWeightG: args.servingWeightG,
      calories: args.calories,
      proteinG: args.proteinG,
      carbsG: args.carbsG,
      fatG: args.fatG,
      fiberG: args.fiberG,
      additionalNutrientsJson: args.additionalNutrientsJson,
      barcode: args.barcode,
      source: args.source,
      favorite: args.favorite ?? false,
      createdAt: now,
      updatedAt: now,
      lastUsedAt: now,
      timesUsed: 0,
    };
    localStore.setQuery(api.customFoods.list, {}, [tempRow, ...list]);
  });

  // ── Recipe mutations ──

  const updateRecipe = useMutation(api.recipes.update).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.recipes.list, {});
    if (list === undefined) return;
    localStore.setQuery(
      api.recipes.list,
      {},
      list.map((row) =>
        row._id === args.id
          ? {
              ...row,
              ...(args.name === undefined ? {} : { name: args.name }),
              ...(args.servings === undefined ? {} : { servings: args.servings }),
            }
          : row,
      ),
    );
  });

  const removeRecipe = useMutation(api.recipes.remove).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.recipes.list, {});
    if (list !== undefined) localStore.setQuery(api.recipes.list, {}, removeRowById(list, args.id));
    const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
    if (favs !== undefined) {
      localStore.setQuery(
        api.savedMeals.listFavorites,
        {},
        favs.filter((fav) => !(fav.kind === "recipe" && fav.refId === args.id)),
      );
    }
  });

  const toggleRecipeFavorite = useMutation(api.recipes.toggleFavorite).withOptimisticUpdate(
    (localStore, args) => {
      const list = localStore.getQuery(api.recipes.list, {});
      if (list === undefined) return;
      const row = list.find((r) => r._id === args.id);
      localStore.setQuery(api.recipes.list, {}, toggleFavoriteInRows(list, args.id));
      const favs = localStore.getQuery(api.savedMeals.listFavorites, {});
      if (favs !== undefined && row !== undefined) {
        const favorited = !row.favorite;
        const rest = favs.filter((fav) => !(fav.kind === "recipe" && fav.refId === args.id));
        localStore.setQuery(
          api.savedMeals.listFavorites,
          {},
          favorited
            ? [
                {
                  kind: "recipe" as const,
                  refId: args.id,
                  name: row.name,
                  calories: recipePerServing(row)?.calories,
                  servingLabel: row.servings === undefined ? undefined : "1 serving",
                },
                ...rest,
              ]
            : rest,
        );
      }
    },
  );

  const createRecipe = useMutation(api.recipes.create).withOptimisticUpdate((localStore, args) => {
    const list = localStore.getQuery(api.recipes.list, {});
    if (list === undefined) return;
    const now = Date.now();
    const tempId = `temp_${now}_${Math.random().toString(36).slice(2)}`;
    // Optimistic totals mirror recipeTotals: a nutrient is defined only when
    // EVERY ingredient defines it; complete requires all five.
    const nutrientKeys = ["calories", "proteinG", "carbsG", "fatG", "fiberG"] as const;
    const totals: Record<string, number | undefined> = {};
    for (const key of nutrientKeys) {
      totals[key] = args.ingredients.every((ingredient) => ingredient[key] !== undefined)
        ? args.ingredients.reduce((sum, ingredient) => sum + (ingredient[key] ?? 0), 0)
        : undefined;
    }
    const tempRow = {
      _id: tempId as Id<"recipes">,
      _creationTime: now,
      userId: tempId as unknown as Id<"users">,
      name: args.name,
      nameLower: args.name.toLowerCase(),
      ingredients: args.ingredients,
      servings: args.servings,
      totalCookedWeightG: args.totalCookedWeightG,
      favorite: args.favorite ?? false,
      createdAt: now,
      updatedAt: now,
      lastUsedAt: now,
      timesUsed: 0,
      totals: {
        calories: totals.calories,
        proteinG: totals.proteinG,
        carbsG: totals.carbsG,
        fatG: totals.fatG,
        fiberG: totals.fiberG,
        complete: nutrientKeys.every((key) => totals[key] !== undefined),
      },
    };
    localStore.setQuery(api.recipes.list, {}, [tempRow, ...list]);
  });

  // ── View callbacks ──

  const reportError = (operation: string, err: unknown) => {
    console.error(`[Frontend][Library] ${operation} failed`, {
      error: err instanceof Error ? err.message : String(err),
    });
  };

  const handleStartReview = (items: readonly LoggingFlowReviewItem[]) => {
    startReviewWithItems(startFlow, items);
  };

  const handleAddToPlate = (item: NewPlateItem) => {
    console.log("[Frontend][Library] Adding item to plate", { name: item.name });
    addItem(item);
  };

  const handleToggleFavorite = (target: FavoriteToggleTarget) => {
    console.log("[Frontend][Library] Toggling favourite", {
      kind: target.kind,
      id: target.id,
      favorite: !target.favorite,
    });
    const run = (): Promise<unknown> => {
      switch (target.kind) {
        case "savedMeal":
          return toggleSavedMealFavorite({ id: target.id as Id<"savedMeals"> });
        case "customFood":
          return toggleCustomFoodFavorite({ id: target.id as Id<"customFoods"> });
        case "recipe":
          return toggleRecipeFavorite({ id: target.id as Id<"recipes"> });
        case "foodMemory":
          return setMemoryFavorite({ id: target.id as Id<"foodMemories">, favorite: !target.favorite });
      }
    };
    run().catch((err: unknown) => reportError("Favourite toggle", err));
  };

  const handleSaveMemory = async (payload: MemorySavePayload): Promise<boolean> => {
    setBusy(true);
    try {
      if (payload.id !== undefined) {
        console.log("[Frontend][Library] Updating memory", { id: payload.id });
        await updateMemory({
          id: payload.id as Id<"foodMemories">,
          calories: payload.calories,
          servingSize: payload.servingSize,
          notes: payload.notes,
        });
      } else {
        console.log("[Frontend][Library] Adding memory", { name: payload.foodName });
        await addManual({
          foodName: payload.foodName,
          calories: payload.calories,
          servingSize: payload.servingSize,
          notes: payload.notes,
        });
      }
      return true;
    } catch (err) {
      reportError("Memory save", err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteMemory = async (id: string): Promise<boolean> => {
    setBusy(true);
    console.log("[Frontend][Library] Deleting memory", { id });
    try {
      await removeMemory({ id: id as Id<"foodMemories"> });
      return true;
    } catch (err) {
      reportError("Memory delete", err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleEditEntity = async (
    target: EntityEditTarget,
    payload: EntityEditPayload,
  ): Promise<boolean> => {
    setBusy(true);
    console.log("[Frontend][Library] Saving entity edit", { kind: target.kind, id: target.id });
    try {
      switch (target.kind) {
        case "savedMeal":
          await renameSavedMeal({ id: target.id as Id<"savedMeals">, name: payload.name });
          break;
        case "customFood":
          await updateCustomFood({
            id: target.id as Id<"customFoods">,
            name: payload.name,
            servingUnit: payload.servingUnit,
            calories: payload.calories,
          });
          break;
        case "recipe":
          await updateRecipe({
            id: target.id as Id<"recipes">,
            name: payload.name,
            servings: payload.servings,
          });
          break;
      }
      return true;
    } catch (err) {
      reportError("Entity edit", err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = (kind: "customFood" | "recipe", id: string) => {
    console.log("[Frontend][Library] Duplicating", { kind, id });
    const run = async (): Promise<unknown> => {
      if (kind === "customFood") {
        const food = (customFoods ?? []).find((row) => row._id === id);
        if (food === undefined) {
          console.warn("[Frontend][Library] Duplicate skipped: custom food not loaded", { id });
          return null;
        }
        return createCustomFood({
          name: `${food.name} (copy)`,
          brand: food.brand,
          servingUnit: food.servingUnit,
          servingWeightG: food.servingWeightG,
          calories: food.calories,
          proteinG: food.proteinG,
          carbsG: food.carbsG,
          fatG: food.fatG,
          fiberG: food.fiberG,
          additionalNutrientsJson: food.additionalNutrientsJson,
          source: food.source as "manual" | "label" | "barcode",
        });
      }
      const recipe = (recipes ?? []).find((row) => row._id === id);
      if (recipe === undefined) {
        console.warn("[Frontend][Library] Duplicate skipped: recipe not loaded", { id });
        return null;
      }
      return createRecipe({
        name: `${recipe.name} (copy)`,
        ingredients: recipe.ingredients.map((ingredient) => ({ ...ingredient })),
        servings: recipe.servings,
        totalCookedWeightG: recipe.totalCookedWeightG,
      });
    };
    run().catch((err: unknown) => reportError("Duplicate", err));
  };

  const handleDelete = async (target: DeleteTarget): Promise<boolean> => {
    setBusy(true);
    console.log("[Frontend][Library] Deleting entity", { kind: target.kind, id: target.id });
    try {
      switch (target.kind) {
        case "savedMeal":
          await removeSavedMeal({ id: target.id as Id<"savedMeals"> });
          break;
        case "customFood":
          await removeCustomFood({ id: target.id as Id<"customFoods"> });
          break;
        case "recipe":
          await removeRecipe({ id: target.id as Id<"recipes"> });
          break;
        case "foodMemory":
          await removeMemory({ id: target.id as Id<"foodMemories"> });
          break;
      }
      return true;
    } catch (err) {
      reportError("Delete", err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const memories = (debouncedSearch ? searchResults : allMemories) as MemoryView[] | undefined;

  return (
    <LibraryView
      search={search}
      onSearchChange={setSearch}
      recentFoods={recentFoods}
      frequentFoods={frequentFoods}
      savedMeals={savedMeals}
      memories={memories}
      favorites={favorites}
      customFoods={customFoods}
      recipes={recipes}
      yesterdayEntries={yesterdayEntries}
      busy={busy}
      onStartReview={handleStartReview}
      onAddToPlate={handleAddToPlate}
      onToggleFavorite={handleToggleFavorite}
      onSaveMemory={handleSaveMemory}
      onDeleteMemory={handleDeleteMemory}
      onEditEntity={handleEditEntity}
      onDuplicate={handleDuplicate}
      onDelete={handleDelete}
    />
  );
}
